import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { api, API_ERROR_ACTION_RESERVED_ADMIN } from "../../../lib/axios";
import { useAuth } from "../../../context/AuthContext";
import { hasPermission } from "../../../config/adminRoutes";

const RESERVE_ADMIN_TOOLTIP = "Action réservée administrateur";

type ZoneOperationalStatus = "ACTIVE" | "SATURATED" | "BLOCKED" | "MAINTENANCE" | "RISK_AREA" | "ARCHIVED";
type ZoneRiskLevel = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
type ZoneSlaPriority = "LOW" | "STANDARD" | "HIGH" | "URGENT";

type ZoneRow = {
  id: number;
  code?: string | null;
  nom: string;
  description?: string | null;
  actif: boolean;
  status?: ZoneOperationalStatus;
  riskLevel?: ZoneRiskLevel;
  slaPriority?: ZoneSlaPriority;
  quartierLibre?: string | null;
  dailyCapacity?: number | null;
  maxTeams?: number | null;
  averagePassageMin?: number | null;
  notes?: string | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  centreLatitude?: number | null;
  centreLongitude?: number | null;
  color?: string | null;
  couleur?: string | null;
  province?: string | null;
  ville?: string | null;
  communes?: { id: number; nom: string }[];
  /** Présent quand l’API liste n’a pas joint le User (repli DB). */
  responsableId?: number | null;
  responsable?: { id: number; nom: string; prenom?: string } | null;
  _count?: {
    clients?: number;
    equipes?: number;
    passagesAbonnement?: number;
    tournees?: number;
  };
};

type ZoneOperationalSummaryDto = {
  zoneId: number;
  clientsActive: number;
  passagesToday: number;
  activeTournees: number;
  latePassages: number;
  incidents: number;
  capacityUsed: number;
  capacityTotal: number | null;
  gpsCoverageStatus: "UNKNOWN" | "CENTER_DEFINED" | "NO_CENTER";
};

type GeoOption = { id: number; nom: string };

type UserOption = { id: number; nom: string; prenom?: string | null };

const todayIso = new Date().toISOString().slice(0, 10);

const STATUS_LABEL: Record<ZoneOperationalStatus, string> = {
  ACTIVE: "Active",
  SATURATED: "Saturée",
  BLOCKED: "Bloquée",
  MAINTENANCE: "Maintenance",
  RISK_AREA: "Zone à risque",
  ARCHIVED: "Archivée",
};

const RISK_LABEL: Record<ZoneRiskLevel, string> = {
  LOW: "Zone normale",
  MEDIUM: "Accès à surveiller",
  HIGH: "Zone difficile",
  CRITICAL: "Zone critique",
};

const SLA_LABEL: Record<ZoneSlaPriority, string> = {
  LOW: "Basse",
  STANDARD: "Standard",
  HIGH: "Haute",
  URGENT: "Urgente",
};

function isGpsInvalid(latitude: string, longitude: string): boolean {
  if (!latitude.trim() && !longitude.trim()) return false;
  const lat = Number(latitude);
  const lng = Number(longitude);
  return !Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180;
}

function displayZoneCode(zone: Pick<ZoneRow, "id" | "code">): string {
  const code = String(zone.code ?? "").trim().toUpperCase();
  if (/^ZN\d{5}$/.test(code)) return code;
  return `ZN${String(zone.id).padStart(5, "0")}`;
}

function formatLoadError(reason: unknown, fallback: string): string {
  const ax = reason as {
    response?: { data?: { message?: string; error?: string; details?: string } };
  };
  const d = ax.response?.data;
  if (d && typeof d.message === "string" && d.message.trim()) return d.message.trim();
  if (d && typeof d.error === "string" && d.error.trim()) {
    const e = d.error.trim();
    if (d.details && typeof d.details === "string") return `${e}: ${d.details.slice(0, 400)}`;
    return e;
  }
  return fallback;
}

export default function ZonesOperationnellesPage(): React.JSX.Element {
  const { user } = useAuth();
  const canManageZones = hasPermission(user, "zones.manage");
  const canDeleteZones = hasPermission(user, "zones.delete");

  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [zonesLoadError, setZonesLoadError] = useState<string | null>(null);
  const [provincesLoadError, setProvincesLoadError] = useState<string | null>(null);
  const [passagesLoadError, setPassagesLoadError] = useState<string | null>(null);
  const [summaryLoadError, setSummaryLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<"ALL" | "ACTIF" | "INACTIF">("ALL");
  const [villeFilter, setVilleFilter] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [responsableFilter, setResponsableFilter] = useState("");
  const [passagesJour, setPassagesJour] = useState(0);
  const [summary, setSummary] = useState<ZoneOperationalSummaryDto[]>([]);

  const [provinces, setProvinces] = useState<GeoOption[]>([]);
  const [villes, setVilles] = useState<GeoOption[]>([]);
  const [communes, setCommunes] = useState<GeoOption[]>([]);
  const [quartiers, setQuartiers] = useState<GeoOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);

  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState<ZoneRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nom: "",
    code: "",
    description: "",
    provinceId: "",
    villeId: "",
    communeId: "",
    quartierId: "",
    quartierNom: "",
    responsableId: "",
    status: "ACTIVE" as ZoneOperationalStatus,
    riskLevel: "LOW" as ZoneRiskLevel,
    slaPriority: "STANDARD" as ZoneSlaPriority,
    dailyCapacity: "",
    maxTeams: "",
    averagePassageMin: "",
    couleur: "",
    centreLatitude: "",
    centreLongitude: "",
    notes: "",
  });

  const closeZoneModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  useEffect(() => {
    if (!openModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeZoneModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openModal, closeZoneModal]);

  async function fetchZonesAndPassages(): Promise<void> {
    setLoading(true);
    setZonesLoadError(null);
    setProvincesLoadError(null);
    setPassagesLoadError(null);
    setSummaryLoadError(null);

    const settled = await Promise.allSettled([
      api.get("/zones", { params: { limit: 500 } }),
      api.get("/geographie/provinces"),
      api.get("/passages/summary", { params: { date: todayIso } }),
      api.get("/zones/operational-summary"),
      api.get("/users").catch(() => ({ data: [] as unknown[] })),
    ]);

    const [zonesRes, provRes, sumRes, operationalSummaryRes, usersRes] = settled;

    if (zonesRes.status === "fulfilled") {
      const d = zonesRes.value.data as ZoneRow[] | { data?: ZoneRow[] };
      const z = Array.isArray(d) ? d : d?.data ?? [];
      setZones(z);
    } else {
      setZones([]);
      setZonesLoadError(formatLoadError(zonesRes.reason, "Impossible de charger les zones."));
    }

    if (provRes.status === "fulfilled") {
      const d = provRes.value.data;
      setProvinces(Array.isArray(d) ? d : []);
    } else {
      setProvinces([]);
      setProvincesLoadError(
        formatLoadError(provRes.reason, "Référentiel géographique indisponible.")
      );
    }

    if (sumRes.status === "fulfilled") {
      const c = (sumRes.value.data as { count?: number })?.count;
      setPassagesJour(typeof c === "number" ? c : 0);
    } else {
      setPassagesJour(0);
      setPassagesLoadError(
        formatLoadError(sumRes.reason, "Impossible de compter les passages du jour.")
      );
    }

    if (operationalSummaryRes.status === "fulfilled") {
      const raw = operationalSummaryRes.value.data as ZoneOperationalSummaryDto[] | { data?: ZoneOperationalSummaryDto[] };
      setSummary(Array.isArray(raw) ? raw : raw.data ?? []);
    } else {
      setSummary([]);
      setSummaryLoadError(formatLoadError(operationalSummaryRes.reason, "Synthèse opérationnelle indisponible."));
    }

    if (usersRes.status === "fulfilled") {
      const raw = usersRes.value as { data?: unknown };
      const u = Array.isArray(raw.data) ? raw.data : [];
      setUsers(
        u.map((x: { id: number; nom: string; prenom?: string | null }) => ({
          id: x.id,
          nom: x.nom,
          prenom: x.prenom,
        }))
      );
    } else {
      setUsers([]);
    }

    setLoading(false);
  }

  useEffect(() => {
    void fetchZonesAndPassages();
  }, []);

  useEffect(() => {
    const pid = form.provinceId ? Number(form.provinceId) : null;
    if (!pid) {
      setVilles([]);
      setCommunes([]);
      setQuartiers([]);
      return;
    }
    void api
      .get<GeoOption[]>("/geographie/villes", { params: { provinceId: pid } })
      .then(({ data }) => setVilles(Array.isArray(data) ? data : []))
      .catch(() => setVilles([]));
  }, [form.provinceId]);

  useEffect(() => {
    const vid = form.villeId ? Number(form.villeId) : null;
    if (!vid) {
      setCommunes([]);
      setQuartiers([]);
      return;
    }
    void api
      .get<GeoOption[]>("/geographie/communes", { params: { villeId: vid } })
      .then(({ data }) => setCommunes(Array.isArray(data) ? data : []))
      .catch(() => setCommunes([]));
  }, [form.villeId]);

  useEffect(() => {
    const cid = form.communeId ? Number(form.communeId) : null;
    if (!cid) {
      setQuartiers([]);
      return;
    }
    void api
      .get<GeoOption[]>("/geographie/quartiers", { params: { communeId: cid } })
      .then(({ data }) => setQuartiers(Array.isArray(data) ? data : []))
      .catch(() => setQuartiers([]));
  }, [form.communeId]);

  /** Libellé responsable quand l’API ne joint pas `responsable` mais envoie `responsableId`. */
  const zonesDisplay = useMemo(() => {
    return zones.map((z) => {
      if (z.responsable) return z;
      const rid = z.responsableId;
      if (rid == null) return z;
      const u = users.find((x) => x.id === rid);
      if (!u) return z;
      return {
        ...z,
        responsable: {
          id: u.id,
          nom: u.nom,
          prenom: u.prenom ?? undefined,
        },
      };
    });
  }, [zones, users]);

  const summaryByZoneId = useMemo(() => {
    return new Map(summary.map((item) => [item.zoneId, item]));
  }, [summary]);

  const kpis = useMemo(() => {
    const actifs = zonesDisplay.filter((z) => z.actif).length;
    const clients =
      summary.reduce((acc, z) => acc + z.clientsActive, 0) ||
      zonesDisplay.reduce((acc, z) => acc + (z._count?.clients ?? 0), 0);
    const equipes = zonesDisplay.reduce((acc, z) => acc + (z._count?.equipes ?? 0), 0);
    const capacityTotal = summary.reduce((acc, z) => acc + (z.capacityTotal ?? 0), 0);
    const capacityUsed = summary.reduce((acc, z) => acc + z.capacityUsed, 0);
    return { actifs, clients, equipes, passages: passagesJour, capacityTotal, capacityUsed };
  }, [zonesDisplay, passagesJour, summary]);

  const rows = useMemo(() => {
    return zonesDisplay.filter((z) => {
      const q = search.trim().toLowerCase();
      const txt = `${z.code ?? ""} ${z.nom} ${z.description ?? ""} ${z.status ?? ""} ${z.riskLevel ?? ""} ${z.ville ?? ""} ${z.province ?? ""} ${
        z.responsable?.nom ?? ""
      } ${z.responsable?.prenom ?? ""}`.toLowerCase();
      if (q && !txt.includes(q)) return false;
      if (statut === "ACTIF" && !z.actif) return false;
      if (statut === "INACTIF" && z.actif) return false;
      if (villeFilter && (z.ville ?? "") !== villeFilter) return false;
      if (
        communeFilter &&
        !(z.communes ?? []).some((c) => c.nom.toLowerCase() === communeFilter.toLowerCase())
      )
        return false;
      if (responsableFilter) {
        const r = `${z.responsable?.nom ?? ""} ${z.responsable?.prenom ?? ""}`.trim().toLowerCase();
        if (!r.includes(responsableFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [zonesDisplay, search, statut, villeFilter, communeFilter, responsableFilter]);

  function openCreate(): void {
    setEditing(null);
    setVilles([]);
    setCommunes([]);
    setQuartiers([]);
    setForm({
      nom: "",
      code: "",
      description: "",
      provinceId: "",
      villeId: "",
      communeId: "",
      quartierId: "",
      quartierNom: "",
      responsableId: "",
      status: "ACTIVE",
      riskLevel: "LOW",
      slaPriority: "STANDARD",
      dailyCapacity: "",
      maxTeams: "",
      averagePassageMin: "",
      couleur: "",
      centreLatitude: "",
      centreLongitude: "",
      notes: "",
    });
    setOpenModal(true);
  }

  async function openEdit(zone: ZoneRow): Promise<void> {
    setEditing(zone);
    setOpenModal(true);
    setVilles([]);
    setCommunes([]);
    setQuartiers([]);
    try {
      const { data } = await api.get<{
        provinceId: number | null;
        villeId: number | null;
        communeIds: number[];
        quartierId: number | null;
        quartierNom: string;
        description: string;
        couleur: string;
        color?: string | null;
        centreLatitude: number | null;
        centreLongitude: number | null;
        centerLatitude?: number | null;
        centerLongitude?: number | null;
        responsable: { id: number } | null;
        actif: boolean;
        nom: string;
        code?: string | null;
        status?: ZoneOperationalStatus;
        riskLevel?: ZoneRiskLevel;
        slaPriority?: ZoneSlaPriority;
        quartierLibre?: string | null;
        dailyCapacity?: number | null;
        maxTeams?: number | null;
        averagePassageMin?: number | null;
        notes?: string | null;
      }>(`/zones/${zone.id}`);
      const firstCommune = data.communeIds?.[0];
      setForm({
        nom: data.nom ?? "",
        code: data.code ?? "",
        description: data.description ?? "",
        provinceId: data.provinceId ? String(data.provinceId) : "",
        villeId: data.villeId ? String(data.villeId) : "",
        communeId: firstCommune ? String(firstCommune) : "",
        quartierId: data.quartierId ? String(data.quartierId) : "",
        quartierNom: data.quartierLibre ?? data.quartierNom ?? "",
        responsableId:
          data.responsable?.id != null
            ? String(data.responsable.id)
            : (data as { responsableId?: number | null }).responsableId != null
              ? String((data as { responsableId?: number | null }).responsableId)
              : "",
        status: data.status ?? (data.actif ? "ACTIVE" : "ARCHIVED"),
        riskLevel: data.riskLevel ?? "LOW",
        slaPriority: data.slaPriority ?? "STANDARD",
        dailyCapacity: data.dailyCapacity != null ? String(data.dailyCapacity) : "",
        maxTeams: data.maxTeams != null ? String(data.maxTeams) : "",
        averagePassageMin: data.averagePassageMin != null ? String(data.averagePassageMin) : "",
        couleur: data.color ?? data.couleur ?? "",
        centreLatitude:
          (data.centerLatitude ?? data.centreLatitude) != null && !Number.isNaN(data.centerLatitude ?? data.centreLatitude)
            ? String(data.centerLatitude ?? data.centreLatitude)
            : "",
        centreLongitude:
          (data.centerLongitude ?? data.centreLongitude) != null && !Number.isNaN(data.centerLongitude ?? data.centreLongitude)
            ? String(data.centerLongitude ?? data.centreLongitude)
            : "",
        notes: data.notes ?? "",
      });
    } catch {
      toast.error("Impossible de charger le détail de la zone.");
      setForm((f) => ({
        ...f,
        nom: zone.nom,
        code: zone.code ?? "",
        description: zone.description ?? "",
        responsableId: zone.responsable?.id ? String(zone.responsable.id) : "",
        status: zone.status ?? (zone.actif ? "ACTIVE" : "ARCHIVED"),
      }));
    }
  }

  async function saveZone(): Promise<void> {
    if (!canManageZones) return;
    if (!form.nom.trim()) {
      toast.warning("Nom de zone obligatoire.");
      return;
    }
    if (!form.provinceId) {
      toast.warning("La province est obligatoire pour enregistrer une zone opérationnelle.");
      return;
    }
    if (!form.communeId) {
      toast.warning("Sélectionnez au moins une commune (référentiel géographique).");
      return;
    }
    if (isGpsInvalid(form.centreLatitude, form.centreLongitude)) {
      toast.error("Coordonnées GPS invalides.");
      return;
    }
    setSaving(true);
    const communeIds = [Number(form.communeId)];
    const payload: Record<string, unknown> = {
      nom: form.nom.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      provinceId: Number(form.provinceId),
      villeId: form.villeId ? Number(form.villeId) : undefined,
      communeIds,
      quartierId: form.quartierId ? Number(form.quartierId) : undefined,
      quartierNom: form.quartierNom.trim() || undefined,
      quartierLibre: form.quartierNom.trim() || undefined,
      status: form.status,
      riskLevel: form.riskLevel,
      slaPriority: form.slaPriority,
      dailyCapacity: form.dailyCapacity.trim() ? Number(form.dailyCapacity) : undefined,
      maxTeams: form.maxTeams.trim() ? Number(form.maxTeams) : undefined,
      averagePassageMin: form.averagePassageMin.trim() ? Number(form.averagePassageMin) : undefined,
      actif: form.status !== "ARCHIVED",
      couleur: form.couleur.trim() || undefined,
      color: form.couleur.trim() || undefined,
      centreLatitude: form.centreLatitude.trim() ? Number(form.centreLatitude) : undefined,
      centreLongitude: form.centreLongitude.trim() ? Number(form.centreLongitude) : undefined,
      centerLatitude: form.centreLatitude.trim() ? Number(form.centreLatitude) : undefined,
      centerLongitude: form.centreLongitude.trim() ? Number(form.centreLongitude) : undefined,
      notes: form.notes.trim() || undefined,
      responsableId: form.responsableId ? Number(form.responsableId) : undefined,
    };
    try {
      if (editing) {
        await api.put(`/zones/${editing.id}`, payload);
        toast.success("Zone mise à jour.");
      } else {
        await api.post("/zones", payload);
        toast.success("Zone créée.");
      }
      setOpenModal(false);
      await fetchZonesAndPassages();
    } catch (e: unknown) {
      const ax = e as {
        response?: { status?: number; data?: { error?: string; message?: string } };
      };
      if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      }
      const msg =
        ax.response?.data?.message ??
        (typeof ax.response?.data?.error === "string" ? ax.response.data.error : null) ??
        "Enregistrement impossible.";
      if (ax.response?.status === 400 && ax.response?.data?.error === "GEO_CHAIN_INVALID") {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleActif(zone: ZoneRow): Promise<void> {
    if (!canManageZones) return;
    try {
      const detail = await api.get(`/zones/${zone.id}`);
      const d = detail.data as {
        provinceId: number;
        communeIds: number[];
        nom: string;
        quartierId?: number | null;
        quartierNom?: string | null;
        description?: string | null;
        couleur?: string | null;
        centreLatitude?: number | null;
        centreLongitude?: number | null;
        responsable?: { id: number } | null;
        villeId?: number | null;
        status?: ZoneOperationalStatus;
        riskLevel?: ZoneRiskLevel;
        slaPriority?: ZoneSlaPriority;
        quartierLibre?: string | null;
        dailyCapacity?: number | null;
        maxTeams?: number | null;
        averagePassageMin?: number | null;
        notes?: string | null;
      };
      await api.put(`/zones/${zone.id}`, {
        nom: d.nom,
        provinceId: d.provinceId,
        villeId: d.villeId ?? undefined,
        communeIds: d.communeIds?.length ? d.communeIds : [],
        quartierId: d.quartierId ?? undefined,
        quartierNom: d.quartierNom ?? undefined,
        description: d.description ?? undefined,
        telephone: (d as { telephone?: string }).telephone,
        email: (d as { email?: string }).email,
        couleur: d.couleur ?? undefined,
        centreLatitude: d.centreLatitude ?? undefined,
        centreLongitude: d.centreLongitude ?? undefined,
        responsableId: d.responsable?.id,
        actif: !zone.actif,
        status: zone.actif ? "ARCHIVED" : "ACTIVE",
        riskLevel: d.riskLevel ?? "LOW",
        slaPriority: d.slaPriority ?? "STANDARD",
        quartierLibre: d.quartierLibre ?? d.quartierNom ?? undefined,
        dailyCapacity: d.dailyCapacity ?? undefined,
        maxTeams: d.maxTeams ?? undefined,
        averagePassageMin: d.averagePassageMin ?? undefined,
        notes: d.notes ?? undefined,
      });
      toast.success(zone.actif ? "Zone désactivée." : "Zone activée.");
      await fetchZonesAndPassages();
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      }
      toast.error("Action impossible.");
    }
  }

  async function removeZone(zone: ZoneRow): Promise<void> {
    if (!canDeleteZones) return;
    const linked =
      (zone._count?.clients ?? 0) > 0 ||
      (zone._count?.equipes ?? 0) > 0 ||
      (zone._count?.passagesAbonnement ?? 0) > 0 ||
      (zone._count?.tournees ?? 0) > 0;
    if (linked) {
      toast.warning("Suppression interdite : la zone est encore utilisée.");
      return;
    }
    try {
      await api.delete(`/zones/${zone.id}`);
      toast.success("Zone supprimée.");
      await fetchZonesAndPassages();
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: { error?: string; message?: string } } };
      if (ax.response?.status === 409) {
        toast.error(ax.response.data?.message ?? ax.response.data?.error ?? "Suppression bloquée.");
      } else if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      } else {
        toast.error("Suppression impossible.");
      }
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-8">
      <header className="mb-6 border-b border-slate-800 pb-4">
        <h1 className="text-2xl font-bold text-white">Zones opérationnelles</h1>
        <p className="mt-1 text-sm text-slate-400">
          Zones terrain pour clients, équipes, tournées et passages — sans édition du référentiel
          administratif ici.
        </p>
        <Link
          to="/admin/geographie"
          className="mt-3 inline-flex rounded-lg border border-sky-700/40 bg-sky-950/30 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-950/50"
        >
          Gérer la géographie
        </Link>
      </header>

      {zonesLoadError || provincesLoadError || passagesLoadError || summaryLoadError ? (
        <div className="mb-4 rounded-xl border border-amber-800/50 bg-amber-950/25 p-4 text-sm text-amber-100">
          <p className="font-medium text-amber-50">Partie des données n’a pas pu être chargée</p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-amber-100/90">
            {zonesLoadError ? <li>Zones : {zonesLoadError}</li> : null}
            {provincesLoadError ? <li>Référentiel : {provincesLoadError}</li> : null}
            {passagesLoadError ? <li>Passages du jour : {passagesLoadError}</li> : null}
            {summaryLoadError ? <li>Synthèse zones : {summaryLoadError}</li> : null}
          </ul>
          <button
            type="button"
            onClick={() => void fetchZonesAndPassages()}
            className="mt-3 rounded-lg border border-amber-600/60 bg-amber-900/40 px-3 py-1.5 text-xs font-semibold text-amber-50 hover:bg-amber-900/60"
          >
            Réessayer
          </button>
        </div>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-500">Zones actives</p>
          <p className="mt-1 text-2xl font-bold text-emerald-300">{kpis.actifs}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-500">Clients rattachés</p>
          <p className="mt-1 text-2xl font-bold text-white">{kpis.clients}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-500">Équipes rattachées</p>
          <p className="mt-1 text-2xl font-bold text-white">{kpis.equipes}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-500">Passages du jour</p>
          <p className="mt-1 text-2xl font-bold text-sky-300">
            {passagesLoadError ? "—" : kpis.passages}
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            Capacité utilisée : {kpis.capacityTotal ? `${kpis.capacityUsed}/${kpis.capacityTotal}` : "non définie"}
          </p>
          {passagesLoadError ? (
            <p className="mt-1 text-[10px] text-amber-400/90">Comptage indisponible</p>
          ) : null}
        </div>
      </section>

      <section className="mt-5 rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex flex-wrap gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Recherche"
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value as "ALL" | "ACTIF" | "INACTIF")}
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          >
            <option value="ALL">Tous statuts</option>
            <option value="ACTIF">Actif</option>
            <option value="INACTIF">Inactif</option>
          </select>
          <input
            value={villeFilter}
            onChange={(e) => setVilleFilter(e.target.value)}
            placeholder="Ville"
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <input
            value={communeFilter}
            onChange={(e) => setCommuneFilter(e.target.value)}
            placeholder="Commune"
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <input
            value={responsableFilter}
            onChange={(e) => setResponsableFilter(e.target.value)}
            placeholder="Responsable"
            className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <button
            type="button"
            disabled={!canManageZones}
            title={!canManageZones ? RESERVE_ADMIN_TOOLTIP : undefined}
            onClick={openCreate}
            className="ml-auto rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Créer une zone
          </button>
        </div>
      </section>

      <section className="mt-5 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/30">
        {loading ? (
          <p className="p-8 text-center text-sm text-slate-500">Chargement...</p>
        ) : zonesLoadError && zones.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-lg font-semibold text-rose-200">Impossible d’afficher les zones</p>
            <p className="mt-2 text-sm text-slate-400">{zonesLoadError}</p>
            <button
              type="button"
              onClick={() => void fetchZonesAndPassages()}
              className="mt-6 rounded-xl border border-slate-600 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-800"
            >
              Réessayer
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-lg font-semibold text-slate-300">Aucune zone opérationnelle</p>
            <button
              type="button"
              disabled={!canManageZones}
              title={!canManageZones ? RESERVE_ADMIN_TOOLTIP : undefined}
              onClick={openCreate}
              className="mt-4 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Créer une zone
            </button>
          </div>
        ) : (
          <table className="min-w-[1280px] w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Nom zone</th>
                <th className="px-4 py-3">Quartier</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Risque</th>
                <th className="px-4 py-3">Responsable</th>
                <th className="px-4 py-3">Clients</th>
                <th className="px-4 py-3">Passages jour</th>
                <th className="px-4 py-3">Tournées actives</th>
                <th className="px-4 py-3">Retards</th>
                <th className="px-4 py-3">Capacité</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((z) => {
                const s = summaryByZoneId.get(z.id);
                const status = z.status ?? (z.actif ? "ACTIVE" : "ARCHIVED");
                const risk = z.riskLevel ?? "LOW";
                const capacityTotal = s?.capacityTotal ?? z.dailyCapacity ?? null;
                const capacityUsed = s?.capacityUsed ?? 0;
                return (
                <tr key={z.id} className="border-b border-slate-800/70">
                  <td className="px-4 py-3 font-mono text-xs text-sky-200">{displayZoneCode(z)}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-white">{z.nom}</p>
                    {z.description ? <p className="text-xs text-slate-500">{z.description}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{z.quartierLibre ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        status === "ACTIVE"
                          ? "bg-emerald-500/20 text-emerald-200"
                          : status === "SATURATED"
                            ? "bg-amber-500/20 text-amber-200"
                            : status === "BLOCKED" || status === "RISK_AREA"
                              ? "bg-rose-500/20 text-rose-200"
                              : "bg-slate-700 text-slate-300"
                      }`}
                    >
                      {STATUS_LABEL[status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{RISK_LABEL[risk]}</td>
                  <td className="px-4 py-3 text-slate-300">
                    {z.responsable
                      ? `${z.responsable.nom} ${z.responsable.prenom ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{s?.clientsActive ?? z._count?.clients ?? 0}</td>
                  <td className="px-4 py-3 text-slate-300">{s?.passagesToday ?? 0}</td>
                  <td className="px-4 py-3 text-slate-300">{s?.activeTournees ?? 0}</td>
                  <td className="px-4 py-3 text-slate-300">{s?.latePassages ?? 0}</td>
                  <td className="px-4 py-3 text-slate-300">{capacityTotal ? `${capacityUsed}/${capacityTotal}` : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Link to={`/admin/zones/${z.id}`} className="rounded-lg border border-sky-700/50 px-2 py-1 text-xs text-sky-200">
                        Voir
                      </Link>
                      <button
                        type="button"
                        disabled={!canManageZones}
                        title={!canManageZones ? RESERVE_ADMIN_TOOLTIP : undefined}
                        onClick={() => void openEdit(z)}
                        className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        disabled={!canManageZones}
                        title={!canManageZones ? RESERVE_ADMIN_TOOLTIP : undefined}
                        onClick={() => void toggleActif(z)}
                        className="rounded-lg border border-amber-700/50 px-2 py-1 text-xs text-amber-200 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {status !== "ARCHIVED" ? "Désactiver" : "Activer"}
                      </button>
                      <Link to={`/admin/carte?zoneId=${z.id}`} className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-200">
                        Carte
                      </Link>
                      <Link to={`/admin/tournees?zoneId=${z.id}`} className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-200">
                        Tournées
                      </Link>
                      <button
                        type="button"
                        disabled={!canDeleteZones}
                        title={!canDeleteZones ? RESERVE_ADMIN_TOOLTIP : undefined}
                        onClick={() => void removeZone(z)}
                        className="rounded-lg border border-rose-700/50 px-2 py-1 text-xs text-rose-200 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {openModal ? (
        <div
          className="fixed inset-0 z-[120] flex min-w-0 items-end justify-center overflow-x-hidden bg-black/70 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="zone-op-modal-title"
        >
          <div className="flex max-h-[90dvh] w-full max-w-5xl min-w-0 flex-col overflow-hidden rounded-t-2xl border border-slate-800 bg-slate-900 shadow-2xl sm:max-h-[min(92dvh,900px)] sm:rounded-2xl">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-800 px-4 py-4 sm:px-5">
              <div className="min-w-0 flex-1 pr-2">
                <h3 id="zone-op-modal-title" className="break-words text-lg font-semibold text-white">
                  {editing ? "Modifier zone opérationnelle" : "Créer zone opérationnelle"}
                </h3>
                <p className="mt-1 break-words text-xs text-slate-500">
                  Province, ville et commune viennent du référentiel. Quartier : liste ou nom libre (complément terrain).
                </p>
              </div>
              <button
                type="button"
                className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white"
                onClick={closeZoneModal}
                aria-label="Fermer"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
              <div className="space-y-5">
              <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h4 className="text-sm font-semibold text-white">A. Identité zone</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} placeholder="Nom métier *" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                  <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="Automatique : ZN00001, ZN00002…" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-sm" />
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ZoneOperationalStatus }))} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm">
                    {Object.entries(STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" rows={2} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm md:col-span-2" />
                </div>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h4 className="text-sm font-semibold text-white">B. Ancrage administratif</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
              <select
                value={form.provinceId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    provinceId: e.target.value,
                    villeId: "",
                    communeId: "",
                    quartierId: "",
                  }))
                }
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="">Province *</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}
                  </option>
                ))}
              </select>
              <select
                value={form.villeId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    villeId: e.target.value,
                    communeId: "",
                    quartierId: "",
                  }))
                }
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="">Ville</option>
                {villes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nom}
                  </option>
                ))}
              </select>
              <select
                value={form.communeId}
                onChange={(e) => setForm((f) => ({ ...f, communeId: e.target.value, quartierId: "" }))}
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="">Commune *</option>
                {communes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
              <select
                value={form.quartierId}
                onChange={(e) => setForm((f) => ({ ...f, quartierId: e.target.value }))}
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="">Quartier (référentiel, optionnel)</option>
                {quartiers.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.nom}
                  </option>
                ))}
              </select>
              <input
                value={form.quartierNom}
                onChange={(e) => setForm((f) => ({ ...f, quartierNom: e.target.value }))}
                placeholder="Quartier (nom libre, optionnel)"
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              />
                </div>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h4 className="text-sm font-semibold text-white">C. Pilotage terrain</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
              <select
                value={form.responsableId}
                onChange={(e) => setForm((f) => ({ ...f, responsableId: e.target.value }))}
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="">Responsable (optionnel)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nom} {u.prenom ?? ""}
                  </option>
                ))}
              </select>
                  <select value={form.riskLevel} onChange={(e) => setForm((f) => ({ ...f, riskLevel: e.target.value as ZoneRiskLevel }))} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm">
                    {Object.entries(RISK_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <select value={form.slaPriority} onChange={(e) => setForm((f) => ({ ...f, slaPriority: e.target.value as ZoneSlaPriority }))} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm">
                    {Object.entries(SLA_LABEL).map(([value, label]) => <option key={value} value={value}>SLA {label}</option>)}
                  </select>
                  <input value={form.dailyCapacity} onChange={(e) => setForm((f) => ({ ...f, dailyCapacity: e.target.value }))} placeholder="Capacité journalière" type="number" min={0} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                  <input value={form.maxTeams} onChange={(e) => setForm((f) => ({ ...f, maxTeams: e.target.value }))} placeholder="Nombre max d’équipes" type="number" min={0} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                  <input value={form.averagePassageMin} onChange={(e) => setForm((f) => ({ ...f, averagePassageMin: e.target.value }))} placeholder="Temps moyen passage (min)" type="number" min={0} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                </div>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h4 className="text-sm font-semibold text-white">D. Localisation</h4>
                <p className="mt-1 text-xs text-slate-500">Exemple : latitude -11.664, longitude 27.479</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <input value={form.centreLatitude} onChange={(e) => setForm((f) => ({ ...f, centreLatitude: e.target.value }))} placeholder="Latitude centre" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                  <input value={form.centreLongitude} onChange={(e) => setForm((f) => ({ ...f, centreLongitude: e.target.value }))} placeholder="Longitude centre" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm" />
                  {isGpsInvalid(form.centreLatitude, form.centreLongitude) ? <p className="text-sm text-rose-300 md:col-span-2">Coordonnées GPS invalides.</p> : null}
                  <div className="flex flex-wrap gap-2 md:col-span-2">
                    <button type="button" className="rounded-lg border border-sky-700/50 px-3 py-1.5 text-xs text-sky-200">Sélectionner sur carte</button>
                    <button type="button" onClick={() => {
                      if (!navigator.geolocation) return toast.warning("Géolocalisation indisponible sur ce navigateur.");
                      navigator.geolocation.getCurrentPosition(
                        (pos) => setForm((f) => ({ ...f, centreLatitude: String(pos.coords.latitude), centreLongitude: String(pos.coords.longitude) })),
                        () => toast.error("Impossible d’utiliser votre position.")
                      );
                    }} className="rounded-lg border border-emerald-700/50 px-3 py-1.5 text-xs text-emerald-200">Utiliser ma position</button>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, centreLatitude: "", centreLongitude: "" }))} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300">Effacer coordonnées</button>
                  </div>
                </div>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h4 className="text-sm font-semibold text-white">E. Apparence / notes</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
              <input
                value={form.couleur}
                onChange={(e) => setForm((f) => ({ ...f, couleur: e.target.value }))}
                placeholder="Couleur zone (hex ou libellé)"
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              />
                  <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes internes" rows={2} className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm md:col-span-2" />
                </div>
              </section>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-800 bg-slate-900/98 px-4 py-4 sm:px-5">
              <button
                type="button"
                onClick={closeZoneModal}
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-600 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 sm:py-2"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={saving || !canManageZones}
                title={!canManageZones ? RESERVE_ADMIN_TOOLTIP : undefined}
                onClick={() => void saveZone()}
                className="inline-flex min-h-11 items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40 sm:py-2"
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
